import { useCallback, useEffect, useRef } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { UseFormReturn } from "react-hook-form";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { Cep } from "../../../../../rh/domain/models/cep";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../../../common/hooks/use_debounced_callback";
import { CentroCustoEndereco } from "../../../../domain/models/centro_custo_endereco";
import { CentroCustoEnderecoForm } from "../../../schemas/CentroCusto_cadastros/centroCustoEndereco";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";

type Props = {
    open: boolean,
    saving: boolean,
    selectedCentroCustoEndereco: CentroCustoEndereco | undefined,
    hookForm: UseFormReturn<CentroCustoEnderecoForm>,
    onSubmit: (form: CentroCustoEnderecoForm) => void,
    onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void,
    onClose: (idCentroCustoEndereco: number | null, openState: boolean) => void,
    onLoadCep: (cep: string) => Promise<Cep | undefined>,

}


export const CadastroCentroCustoEnderecoModal = ({
    open,
    saving,
    selectedCentroCustoEndereco,
    hookForm,
    onLoadCep,
    onSubmit,
    onClose,
    onOpenChangeHistory,

}: Props) => {
    const onLoadCepSkiper = useRef(false);
    const debouncedReff = useCreateDebounceHandler(750);

    const loadCep = useDebouncedCallback(debouncedReff, async (cep: string) => {
        return async () => {
            if (typeof cep === "string") {
                const normalizedCep = cep.trim();
                const requiredSize = 'xx.xxx-xxx';

                if (normalizedCep.length === requiredSize.length) {
                    const cepInfo = await onLoadCep(cep);
                    if (cepInfo) {
                        hookForm.setValue('rua', cepInfo.logradouro.toUpperCase());
                        hookForm.setValue('bairro', cepInfo.bairro.toUpperCase());
                        hookForm.setValue('cidade', cepInfo.cidade.toUpperCase());
                        hookForm.setValue('uf', cepInfo.uf.toUpperCase());
                        hookForm.setValue('pais', cepInfo.pais.toUpperCase());
                    }
                }
                else if (normalizedCep.length === 0) {
                    hookForm.setValue('rua', '');
                    hookForm.setValue('bairro', '');
                    hookForm.setValue('cidade', '');
                    hookForm.setValue('uf', '');
                    hookForm.setValue('pais', '');
                }
            }
        };
    }, [hookForm, onLoadCep]);


    useEffect(() => {
        if (selectedCentroCustoEndereco) {
            onLoadCepSkiper.current = true;
        }

    }, [selectedCentroCustoEndereco]);


    const handleChangeHistory = useCallback(() => {
        if (selectedCentroCustoEndereco) {
            onOpenChangeHistory(selectedCentroCustoEndereco.idCentroCustoEndereco ?? 0, LogTipoTabela.CENTRO_CUSTO_ENDERECO);
        }
    }, [selectedCentroCustoEndereco, onOpenChangeHistory]);

    const handleClose = useCallback(() => {
        if (!saving) onClose(null, false);
    }, [
        saving,
        onClose,
    ]);

    const handleSubmit = useCallback((form: CentroCustoEnderecoForm) => {

        if (!saving) onSubmit(form);
    }, [
        saving,
        onSubmit,
    ]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            slotProps={{
                paper: {
                    component: 'form',
                    onSubmit: hookForm.handleSubmit(handleSubmit)
                }
            }}
            sx={{
                "& .MuiDialog-container": {
                    "& .MuiPaper-root": {
                        width: "100%",
                        maxWidth: "100%",
                    },
                },
            }}
        >
            <DialogTitle>
                {selectedCentroCustoEndereco ? "Editar" : "Cadastrar"} Informações do Endereço
            </DialogTitle>

            <DialogContent>
                <ModalLoading loading={saving}>
                    <FormContainer title={"Informações"} arrangement="fullRow">
                        <>

                            <h3 className="font-bold text-black dark:text-white text-2xl">Endereço de entrega de materiais e serviços</h3>
                            <hr />

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='tipoEndereco'
                                    label='Tipo de Endereço'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <div className="w-full flex flex-row justify-center items-center">
                                    <FormRadioGroupInput
                                        name='principal'
                                        label='Endereço Principal'
                                        options={[
                                            { id: 0, label: 'Não' },
                                            { id: 1, label: 'Sim' },
                                        ]}
                                        reactHookForm={hookForm} />
                                </div>
                            </div>

                            <div className="flex flex-row gap-3">
                                <FormMaskedInput
                                    name='cep'
                                    label={"CEP"}
                                    unmask={false}
                                    mask="00.000-000"
                                    maskPlaceholder="__.___-___"
                                    reactHookForm={hookForm}
                                    onChange={(cep) => {
                                        const normalizedCep = cep.trim().replaceAll('_', '');
                                        const requiredSize = 'xx.xxx-xxx';

                                        if (!onLoadCepSkiper.current) {
                                            if (normalizedCep.length === 0 || normalizedCep.length === requiredSize.length) {
                                                loadCep(cep);
                                            }
                                        }
                                        onLoadCepSkiper.current = false;
                                    }} />


                                <DefaultFormInput
                                    name='cidade'
                                    label='Cidade'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='rua'
                                    label='Rua/Bairro/Alameda'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='numero'
                                    label='Numero'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='apartamento'
                                    label='Complemento'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='bairro'
                                    label='Bairro'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>

                            <div className="flex flex-row gap-3">
                                <DefaultFormInput
                                    name='uf'
                                    label='Estado'
                                    required={false}
                                    reactHookForm={hookForm} />

                                <DefaultFormInput
                                    name='pais'
                                    label='País'
                                    required={false}
                                    reactHookForm={hookForm} />
                            </div>
                        </>

                    </FormContainer>


                </ModalLoading>
            </DialogContent>
            <DialogActions>
                <div className="w-full h-full items-center justify-between flex p-4 gap-4">
                    <div>
                        {
                            isNotNullOrUndefined(selectedCentroCustoEndereco) &&
                            <PrimaryButton
                                buttonColor="yellow"
                                onClick={handleChangeHistory}>
                                <span className="text-xs">
                                    Historico de alterações
                                </span>
                            </PrimaryButton>
                        }
                    </div>

                    <div className="flex flex-row gap-2">
                        <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                            <h1
                                className="text-sm text-black dark:text-white"
                                onClick={handleClose}>
                                Cancelar
                            </h1>
                        </div>

                        <div className="w-[120px]">
                            {saving ? (
                                <PrimaryButton
                                    buttonColor="primary-green">
                                    Salvando...
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton
                                    type="submit"
                                    buttonColor="primary-green">
                                    Salvar
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                </div>
            </DialogActions>
        </Dialog>
    )
}